import React from "react";
import PageWrapper from "@/components/layout/page-wrapper";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { AlignLeft, Pencil, Plus, RotateCcwSquareIcon, Search, SquareMenuIcon, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import useOnboardingData from "@/store/onboarding-data";
import { useCreateTicket } from "@/api/tickets/create-ticket";
import { useGetTickets } from "@/api/tickets/get-tickets";
import { cn, formatDate } from "@/lib/utils";
import PaginationCombo from "@/components/ui/pagination-combo";
import { Skeleton } from "@/components/ui/skeleton";
import NoDataFound from "@/components/layout/no-data-found";
import { useSearchParams } from "react-router";
import { useDeleteTicket } from "@/api/tickets/delete-ticket";
import { useResolveTicket } from "@/api/tickets/resolve-ticket";
import { useUpdateTicket } from "@/api/tickets/update-ticket";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Validation schema for ticket creation
const ticketSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().min(1, "Description is required"),
});

type TicketFormData = z.infer<typeof ticketSchema>;

// Create Ticket Dialog Component with modal closing functionality
function CreateTicketDialogWithClose() {
    const [open, setOpen] = React.useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<TicketFormData>({
        resolver: zodResolver(ticketSchema),
        defaultValues: {
            title: "",
            description: "",
        },
    });

    const { userData } = useOnboardingData();
    const { mutate: createTicket, isPending } = useCreateTicket();

    const onSubmit = (data: TicketFormData) => {
        createTicket(
            {
                title: data.title,
                description: data.description,
                user_id: userData?.id,
            },
            {
                onSuccess: () => {
                    reset(); // Reset form after successful submission
                    setOpen(false); // Close the dialog after successful submission
                },
                onError: (error) => {
                    console.error("Error creating ticket:", error);
                    // Optionally show an error message to the user
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">
                    <Plus className="mr-2 h-4 w-4" /> Add Ticket
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create New Ticket</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input id="title" {...register("title")} placeholder="Enter ticket title" />
                        {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea id="description" {...register("description")} placeholder="Enter ticket description" rows={4} />
                        {errors.description && <p className="text-sm text-red-500">{errors.description.message}</p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                reset();
                                setOpen(false);
                            }}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? "Creating..." : "Create Ticket"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// Delete Ticket Dialog Component
function DeleteTicketDialog({
    ticketId,
    onDelete,
    isOpen,
    onClose,
}: {
    ticketId: number;
    onDelete: () => void;
    isOpen: boolean;
    onClose: () => void;
}) {
    return (
        <AlertDialog open={isOpen} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete ticket ID <strong>{ticketId}</strong>
                        and remove it from our servers.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            onDelete();
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

// Resolve Ticket Dialog Component
function ResolveTicketDialog({
    ticketId,
    onResolve,
    isOpen,
    onClose,
}: {
    ticketId: number;
    onResolve: () => void;
    isOpen: boolean;
    onClose: () => void;
}) {
    return (
        <AlertDialog open={isOpen} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Resolve Ticket</AlertDialogTitle>
                    <AlertDialogDescription>
                        Are you sure you want to resolve ticket ID <strong>{ticketId}</strong>? This action will change the status to resolved.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            onResolve();
                        }}
                    >
                        Resolve
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

// Update Ticket Dialog Component
function UpdateTicketDialog({
    ticket,
    isOpen,
    onClose,
}: {
    ticket: { id: number; title: string; description: string };
    isOpen: boolean;
    onClose: () => void;
}) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<TicketFormData>({
        resolver: zodResolver(ticketSchema),
        defaultValues: {
            title: ticket?.title || "",
            description: ticket?.description || "",
        },
    });

    useOnboardingData();
    const { mutate: updateTicket, isPending } = useUpdateTicket();

    React.useEffect(() => {
        if (ticket) {
            reset({
                title: ticket.title,
                description: ticket.description,
            });
        }
    }, [ticket, reset]);

    const onSubmit = (data: TicketFormData) => {
        updateTicket(
            {
                id: ticket.id,
                payload: {
                    title: data.title,
                    description: data.description,
                },
            },
            {
                onSuccess: () => {
                    reset(); // Reset form after successful submission
                    onClose(); // Close the dialog after successful submission
                },
                onError: (error) => {
                    console.error("Error updating ticket:", error);
                    // Optionally show an error message to the user
                },
            }
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Update Ticket</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input id="title" {...register("title")} placeholder="Enter ticket title" />
                        {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea id="description" {...register("description")} placeholder="Enter ticket description" rows={4} />
                        {errors.description && <p className="text-sm text-red-500">{errors.description.message}</p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                reset();
                                onClose();
                            }}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? "Updating..." : "Update Ticket"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

const BillingTableDropdown = ({ id, status, ticket }: { id: number; status: string; ticket: { id: number; title: string; description: string } }) => {
    const navigate = useNavigate();
    const { mutate: deleteTicket } = useDeleteTicket();
    const { mutate: resolveTicket } = useResolveTicket();
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = React.useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = React.useState(false);

    const handleDelete = () => {
        deleteTicket(id, {
            onSuccess: () => {
                setDeleteDialogOpen(false);
            },
            onError: (error) => {
                console.error("Error deleting ticket:", error);
                setDeleteDialogOpen(false);
            },
        });
    };

    const handleResolve = () => {
        resolveTicket(
            { id },
            {
                onSuccess: () => {
                    setResolveDialogOpen(false);
                },
                onError: (error) => {
                    console.error("Error resolving ticket:", error);
                    setResolveDialogOpen(false);
                },
            }
        );
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size={"sm"} variant="ghost" className="p-2 cursor-pointer">
                        <SquareMenuIcon />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`${id}`)}>
                        <AlignLeft /> Ticket Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setUpdateDialogOpen(true)}>
                        <Pencil /> Update Ticket
                    </DropdownMenuItem>
                    {/* Only show Resolve Ticket option if the ticket status is "open" */}
                    {status === "open" && (
                        <DropdownMenuItem onClick={() => setResolveDialogOpen(true)}>
                            <RotateCcwSquareIcon /> Resolve Ticket
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-red-600 focus:text-red-600">
                        <Trash /> Delete Ticket
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <DeleteTicketDialog ticketId={id} onDelete={handleDelete} isOpen={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
            <ResolveTicketDialog ticketId={id} onResolve={handleResolve} isOpen={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} />
            <UpdateTicketDialog ticket={ticket} isOpen={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} />
        </>
    );
};

const TicketManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const formRef = React.useRef<HTMLFormElement>(null);
    const [selectedStartDate, setSelectedStartDate] = React.useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = React.useState<string>("");

    const {
        data: tickets,
        isLoading,
        isError,
    } = useGetTickets({
        skip: (parseInt(searchParams.get("page") || "1") - 1) * parseInt(searchParams.get("per_page") || "10"),
        limit: parseInt(searchParams.get("per_page") || "10"),
        search: searchParams.get("search") || null,
        status: searchParams.get("status") || null,
        start_date: searchParams.get("start_date") || null,
        end_date: searchParams.get("end_date") || null,
    });

    React.useEffect(() => {
        if (searchParams.get("search") && formRef.current) {
            formRef.current.search.value = searchParams.get("search") || "";
        }

        // Set the initial date input values based on URL parameters
        const urlStartDate = searchParams.get("start_date") || "";
        const urlEndDate = searchParams.get("end_date") || "";

        setSelectedStartDate(urlStartDate);
        setSelectedEndDate(urlEndDate);
    }, [searchParams]);

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const searchValue = form.search.value.trim();

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (selectedStartDate) {
            params.start_date = selectedStartDate;
        }

        if (selectedEndDate) {
            params.end_date = selectedEndDate;
        }

        // Include status if it's not the default "all"
        if (searchParams.get("status") && searchParams.get("status") !== "all") {
            params.status = searchParams.get("status") || "";
        }

        setSearchParams(params);
    };

    // Function to handle date filter specifically
    const handleDateFilter = () => {
        const searchValue = formRef.current?.search.value.trim() || "";
        const statusValue = searchParams.get("status") || "all";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (selectedStartDate) {
            params.start_date = selectedStartDate;
        }

        if (selectedEndDate) {
            params.end_date = selectedEndDate;
        }

        if (statusValue !== "all") {
            params.status = statusValue;
        }

        setSearchParams(params);
    };

    return (
        <PageWrapper pageTitle="Tickets" currentPage="Tickets">
            <div className="flex justify-end gap-2 items-center mb-4 flex-wrap">
                <div className="flex border rounded-md">
                    <form ref={formRef} className="flex" onSubmit={handleSearchSubmit}>
                        <Input placeholder="Search tickets..." className="border-none" name="search" />
                        <Button variant={"ghost"} type="submit">
                            <Search />
                        </Button>
                    </form>
                </div>

                <Select
                    onValueChange={(value) => {
                        if (value === "all") {
                            // When "all" is selected, remove the status parameter from URL
                            const { status, ...rest } = Object.fromEntries(searchParams.entries());
                            setSearchParams({ ...rest, page: "1" });
                        } else {
                            setSearchParams({ status: value, page: "1" });
                        }
                    }}
                    value={searchParams.get("status") || "all"}
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectLabel>Status</SelectLabel>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>

                <div className="flex gap-2">
                    <Input
                        type="date"
                        placeholder="Start date"
                        className="w-36"
                        value={selectedStartDate}
                        onChange={(e) => setSelectedStartDate(e.target.value)}
                    />
                    <Input
                        type="date"
                        placeholder="End date"
                        className="w-36"
                        value={selectedEndDate}
                        min={selectedStartDate || undefined}
                        onChange={(e) => setSelectedEndDate(e.target.value)}
                    />
                    <Button type="button" onClick={handleDateFilter}>
                        Filter
                    </Button>
                </div>

                {(searchParams.get("search") || searchParams.get("status") || searchParams.get("start_date") || searchParams.get("end_date")) && (
                    <Button
                        onClick={() => {
                            const params: Record<string, string> = {
                                page: "1",
                                per_page: "10",
                            };
                            setSearchParams(params);
                            if (formRef.current) {
                                formRef.current.search.value = "";
                            }
                            // Reset date inputs
                            setSelectedStartDate("");
                            setSelectedEndDate("");
                        }}
                    >
                        Clear
                    </Button>
                )}
                <CreateTicketDialogWithClose />
            </div>

            <Card className="w-full mx-auto p-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <Checkbox />
                            </TableHead>
                            <TableHead>Ticket ID</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date Created</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="flex justify-end">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading &&
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                                <TableRow key={item}>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-4" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-20" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-full" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-16" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-24" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-full" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <div className="flex justify-end">
                                            <Skeleton className="h-8 w-8" />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        {isError && (
                            <TableRow>
                                <TableCell colSpan={7} className="w-full text-center">
                                    <NoDataFound message="Failed to load tickets" />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading && !isError && tickets?.data && tickets.data.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="w-full text-center">
                                    <NoDataFound message="No tickets found" />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading &&
                            !isError &&
                            tickets?.data &&
                            tickets.data.map((ticket: { id: number; title: string; description: string; status: string; created_at: string }) => (
                                <TableRow key={ticket.id} className="hover:bg-muted/20">
                                    <TableCell>
                                        <Checkbox />
                                    </TableCell>
                                    <TableCell className="text-blue-600 font-medium">{ticket.id}</TableCell>
                                    <TableCell>{ticket.title}</TableCell>
                                    <TableCell>
                                        <Badge
                                            className={cn(
                                                ticket.status === "open"
                                                    ? "bg-green-200 text-green-800"
                                                    : ticket.status === "resolved"
                                                    ? "bg-blue-200 text-blue-800"
                                                    : ticket.status === "closed"
                                                    ? "bg-gray-200 text-gray-800"
                                                    : "bg-orange-200 text-orange-800"
                                            )}
                                        >
                                            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{formatDate(new Date(ticket.created_at))}</TableCell>
                                    <TableCell className="w-72 max-w-xs truncate">
                                        {ticket.description.length > 50 ? ticket.description.substring(0, 50) + "..." : ticket.description}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex justify-end gap-2">
                                            <BillingTableDropdown id={ticket.id} status={ticket.status} ticket={ticket} />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>

                {!isLoading && !isError && tickets?.pagination && <PaginationCombo totalItems={tickets.pagination.total_entity} />}
            </Card>
        </PageWrapper>
    );
};

export default TicketManagement;
